import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logoIglesia from '../../assets/images/logo-oficial.png';
import { usePublicContent } from '../../hooks/usePublicContent';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const navItems = [
    { name: 'Inicio', path: '/' },
    { name: 'Nosotros', path: '/nosotros' },
    { name: 'Eventos', path: '/eventos' },
    { name: 'Iglesia', path: '/iglesia' },
    { name: 'Fundación', path: '/fundacion' },
    { name: 'Donaciones', path: '/donaciones' },
    { name: 'Contacto', path: '/contacto' },
  ];

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const { getText, getImage, loading } = usePublicContent('HEADER');
  const logoUrl = getImage('logo', 'url', logoIglesia);
  const churchName = getText('brand', 'nombre', 'Iglesia Cristiana Cuadrangular');
  const churchSubtitle = getText('brand', 'subtitulo', 'Central de Apartadó');
  const loginLabel = getText('nav', 'login_label', 'Miembros');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-stone-50/98 backdrop-blur-2xl shadow-xl shadow-stone-900/10 border-b border-stone-300/60' 
          : 'bg-stone-50/85 backdrop-blur-2xl border-b border-stone-200/40'
      }`}
    >
      <div className="mx-auto px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
        <div className="flex justify-between items-center h-20">
          
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative w-16 h-16 rounded-xl overflow-hidden shadow-sm ring-1 ring-stone-200/50 transition-all duration-300 group-hover:shadow-md group-hover:scale-105 group-hover:ring-stone-300/50">
              <img 
                src={logoUrl} 
                alt={churchName} 
                className="w-full h-full object-contain"
              />
            </div>
            <div className="hidden md:block">
              <h1 className="text-[15px] lg:text-base font-serif font-bold text-stone-800 leading-tight tracking-tight transition-colors duration-300 group-hover:text-purple-800">
                {churchName}
              </h1>
              <p className="text-[11px] font-medium text-stone-500 uppercase tracking-[0.2em] leading-tight">
                {churchSubtitle}
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-0.5">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative px-4 py-2 text-sm font-medium tracking-wide transition-colors duration-200 group ${
                  isActive(item.path)
                    ? 'text-purple-800'
                    : 'text-stone-600 hover:text-stone-900'
                }`}
              >
                {item.name}
                {/* Active indicator */}
                <span 
                  className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 rounded-full bg-gradient-to-r from-purple-600 to-red-500 transition-all duration-300 ${
                    isActive(item.path) ? 'w-4/5 opacity-100' : 'w-0 opacity-0 group-hover:w-2/3 group-hover:opacity-100'
                  }`} 
                />
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 bg-purple-800 text-white text-sm font-semibold rounded-xl hover:bg-purple-700 transition-all duration-300 shadow-sm hover:shadow-md hover:shadow-purple-900/20 hover:-translate-y-0.5"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              {loginLabel}
            </Link>
            
            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2.5 rounded-xl text-stone-600 hover:text-stone-900 hover:bg-stone-100 transition-all duration-200"
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`lg:hidden transition-all duration-500 ease-in-out overflow-hidden ${
          isMenuOpen ? 'max-h-[80vh] pb-6' : 'max-h-0'
        }`}>
          <div className="flex flex-col gap-1 mt-2 px-2 py-4 rounded-2xl bg-stone-50/80 border border-stone-200/40 backdrop-blur-xl">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMenuOpen(false)}
                className={`relative px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isActive(item.path)
                    ? 'bg-purple-800 text-white shadow-md'
                    : 'text-stone-600 hover:text-purple-800 hover:bg-purple-50/60'
                }`}
              >
                {item.name}
              </Link>
            ))}
            
            <div className="h-px bg-stone-200/60 my-2 mx-2"></div>
            
            <Link
              to="/login"
              onClick={() => setIsMenuOpen(false)}
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold bg-purple-800 text-white shadow-md hover:bg-purple-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              {loginLabel}
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Header;
